/**
 * @file This file contains functions for converting rule and alert objects to their corresponding text representations.
 * @version 1.0.0
 */

import { Alert, ExternalAlertInfo } from "types/alert";
import { Geofence } from "types/geofence";
import { FieldsBible } from "types/holy-books";
import {
  GeofenceDefinition,
  Rule,
  ThresholdDefinition,
  ThresholdLogic,
} from "types/rule";

const NOT_AVAILABLE_TEXT = "N/A";

/**
 * Converts an alert object to its corresponding text representation.
 *
 * @param alert - The alert object to convert.
 * @param fieldsBible - The fields bible object containing field definitions.
 * @param geofences - The array of geofences.
 * @param usePastTense - A boolean indicating whether to use past tense in the text representation.
 * @returns The text representation of the alert.
 */
export const convertAlertToText = (
  alert: Alert,
  fieldsBible: FieldsBible | null,
  geofences: Geofence[],
  usePastTense: boolean
): string => {
  let text = NOT_AVAILABLE_TEXT;

  if (alert.type === "external") {
    const typedAlert = alert as Alert<ExternalAlertInfo>;
    text = typedAlert?.info?.message || NOT_AVAILABLE_TEXT;
  }

  if (alert.type === "threshold" && alert?.info?.rule) {
    const rule = alert?.info?.rule as Rule<ThresholdDefinition>;

    if (rule.message) return rule.message;

    text = convertThresholdLogicToText(
      rule?.definition?.logic?.and,
      fieldsBible,
      usePastTense
    );
  }

  if (alert.type === "geofence" && alert?.info?.rule) {
    const rule = alert?.info?.rule as Rule<GeofenceDefinition>;

    if (rule.message) return rule.message;

    text = convertGeofenceLogicToText(
      rule?.definition,
      geofences,
      usePastTense
    );
  }

  return text;
};

/**
 * Converts a threshold logic array to its corresponding text representation.
 *
 * @param logicArray - The threshold logic array to convert.
 * @param fieldsBible - The fields bible object containing field definitions.
 * @param usePastTense - A boolean indicating whether to use past tense in the text representation.
 * @returns The text representation of the threshold logic array.
 */
const convertThresholdLogicToText = (
  logicArray: ThresholdLogic[],
  fieldsBible: FieldsBible | null,
  usePastTense: boolean
): string => {
  const text: string[] = [];

  const operationText = {
    ">": {
      past: "was more than",
      present: "more than",
    },
    "<": {
      past: "was less than",
      present: "less than",
    },
  };

  const rangeOperationText = {
    ">": "has increased by",
    "<": "has decreased by",
  };

  logicArray?.forEach((logic) => {
    const operation = Object.keys(logic)[0];
    const condition = logic[operation][0];

    let field = condition.var;
    let threshold = logic[operation][1];
    let unit = "";

    if (fieldsBible && fieldsBible[condition.var]) {
      field = fieldsBible[condition.var].label;
      unit = fieldsBible[condition.var].unit || "";

      // Convert Kelvin to Celsius (temporary solution)
      if (fieldsBible[condition.var].unit === "K") {
        threshold = convertKelvinToCelsius(threshold);
        unit = "°C";
      }

      // Convert Pa to bar (temporary solution)
      if (fieldsBible[condition.var].unit === "Pa") {
        threshold = convertPascalToBar(threshold);
        unit = "bar";
      }
    }

    if (condition.type === "instant") {
      text.push(
        `${field} ${
          operationText[operation][usePastTense ? "past" : "present"]
        } ${threshold} ${unit}`
      );
    }

    if (condition.type === "vfp") {
      text.push(
        `${field} ${
          operationText[operation][usePastTense ? "past" : "present"]
        } ${threshold} ${unit} for ${condition.period_sec} seconds`
      );
    }

    if (condition.type === "range") {
      text.push(
        `${field} ${rangeOperationText[operation]} ${Math.abs(
          threshold
        )} ${unit} within ${condition.period_sec} seconds`
      );
    }
  });

  if (text.length === 0) return NOT_AVAILABLE_TEXT;

  return text.join(" and ");
};

/**
 * Converts a geofence logic object to its corresponding text representation.
 *
 * @param definition - The geofence logic object to convert.
 * @param geofences - The array of geofences.
 * @param usePastTense - A boolean indicating whether to use past tense in the text representation.
 * @returns The text representation of the geofence logic object.
 */
const convertGeofenceLogicToText = (
  definition: GeofenceDefinition,
  geofences: Geofence[],
  usePastTense: boolean
): string => {
  const triggerText = {
    exit: {
      present: "Exiting",
      past: "Exited",
    },
    enter: {
      present: "Entering",
      past: "Entered",
    },
    cross: {
      present: "Crossing",
      past: "Crossed",
    },
  };

  const triggerType = triggerText[definition.trigger_on];

  const geofence = geofences?.find(
    (geofence) => geofence.id === definition.geofences.ids[0]
  );

  if (!triggerType || !geofence) return NOT_AVAILABLE_TEXT;

  const trigger = triggerType[usePastTense ? "past" : "present"];

  return `${trigger} ${geofence.name} boundaries`;
};

/**
 * Converts a temperature value from Kelvin to Celsius.
 *
 * @param kelvin - The temperature value in Kelvin.
 * @returns The temperature value in Celsius rounded to one decimal.
 */
const convertKelvinToCelsius = (kelvin: number): number => {
  return Math.round((kelvin - 273.15) * 10) / 10;
};

/**
 * Converts a pressure value from Pascal to bar.
 *
 * @param pascal - The pressure value in Pascal.
 * @returns The pressure value in bar rounded to one decimal.
 */
const convertPascalToBar = (pascal: number): number => {
  return Math.round((pascal / 100000) * 10) / 10;
};
